import React, { useState } from 'react';
import {
  Leaf,
  Truck,
  Calculator,
  Users,
  BookOpen,
  Handshake,
  ShieldCheck,
  HeartHandshake,
  X,
  Save
} from 'lucide-react';

export default function Beneficios() {
  const [showSolicitudModal, setShowSolicitudModal] = useState(false);
  const [formData, setFormData] = useState({
    socio: '',
    tipoBeneficio: '',
    descripcion: '',
    justificacion: ''
  });

  const beneficios = [
    {
      titulo: 'Insumos Agrícolas',
      descripcion: 'Acceso a insumos de alta calidad a precios preferenciales para socios',
      icon: Leaf,
      color: 'bg-green-100 text-green-600',
    },
    {
      titulo: 'Logística y Transporte',
      descripcion: 'Servicio de transporte y almacenamiento para productos agrícolas',
      icon: Truck,
      color: 'bg-blue-100 text-blue-600',
    },
    {
      titulo: 'Asesoría Financiera',
      descripcion: 'Consultoría especializada en finanzas agrícolas y acceso a créditos',
      icon: Calculator,
      color: 'bg-purple-100 text-purple-600',
    },
    {
      titulo: 'Capacitación',
      descripcion: 'Programas de formación en técnicas agrícolas y gestión empresarial',
      icon: BookOpen,
      color: 'bg-yellow-100 text-yellow-600',
    },
  ];

  const programasEspeciales = [
    {
      titulo: 'Programa de Mentorías',
      descripcion: 'Conexión con agricultores experimentados para guía y apoyo',
      icon: Users,
      estado: 'Abierto',
      cupos: 15,
    },
    {
      titulo: 'Alianzas Comerciales',
      descripcion: 'Acuerdos con empresas del sector para beneficios exclusivos',
      icon: Handshake,
      estado: 'Permanente',
      cupos: null,
    },
    {
      titulo: 'Seguro Agrícola',
      descripcion: 'Cobertura especial para cultivos y equipamiento',
      icon: ShieldCheck,
      estado: 'Disponible',
      cupos: null,
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Nueva solicitud de beneficio:', formData);
    setShowSolicitudModal(false);
    setFormData({
      socio: '',
      tipoBeneficio: '',
      descripcion: '',
      justificacion: ''
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Beneficios para Socios</h1>
          <p className="mt-2 text-gray-600">Descubre todas las ventajas de ser parte de nuestra cooperativa</p>
        </div>
        <button 
          onClick={() => setShowSolicitudModal(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
        >
          <HeartHandshake className="w-5 h-5" />
          Solicitar Beneficio
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {beneficios.map((beneficio, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-6">
            <div className={`${beneficio.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
              <beneficio.icon className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{beneficio.titulo}</h3>
            <p className="text-gray-600">{beneficio.descripcion}</p>
          </div>
        ))}
      </div>

      <div className="bg-green-50 rounded-lg p-8 mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Programas Especiales</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {programasEspeciales.map((programa, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <programa.icon className="w-8 h-8 text-green-600" />
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  {programa.estado}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{programa.titulo}</h3>
              <p className="text-gray-600 mb-4">{programa.descripcion}</p>
              {programa.cupos && (
                <p className="text-sm text-gray-500">
                  Cupos disponibles: <span className="font-medium">{programa.cupos}</span>
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Requisitos y Proceso</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Requisitos para Acceder</h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                Ser socio activo de la cooperativa
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                Estar al día con las cuotas sociales
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                Participación en asambleas regulares
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                Cumplir con los reglamentos internos
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Proceso de Solicitud</h3>
            <ol className="space-y-4">
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-semibold">
                  1
                </span>
                <div>
                  <p className="font-medium text-gray-900">Verificación de Elegibilidad</p>
                  <p className="text-gray-600">Comprobación del estado del socio y cumplimiento de requisitos</p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-semibold">
                  2
                </span>
                <div>
                  <p className="font-medium text-gray-900">Solicitud Formal</p>
                  <p className="text-gray-600">Presentación de la solicitud con documentación requerida</p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-semibold">
                  3
                </span>
                <div>
                  <p className="font-medium text-gray-900">Evaluación y Aprobación</p>
                  <p className="text-gray-600">Revisión por el comité correspondiente</p>
                </div>
              </li>
            </ol>
          </div>
        </div>
      </div>

      {/* Modal para Solicitar Beneficio */}
      {showSolicitudModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Solicitar Beneficio</h2>
              <button
                onClick={() => setShowSolicitudModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Socio Solicitante
                </label>
                <input
                  type="text"
                  name="socio"
                  value={formData.socio}
                  onChange={handleInputChange}
                  required
                  placeholder="Nombre completo del socio"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Beneficio
                </label>
                <select
                  name="tipoBeneficio"
                  value={formData.tipoBeneficio}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Seleccionar beneficio</option>
                  <option value="insumos">Insumos Agrícolas</option>
                  <option value="transporte">Logística y Transporte</option>
                  <option value="asesoria">Asesoría Financiera</option>
                  <option value="capacitacion">Capacitación</option>
                  <option value="mentoria">Programa de Mentorías</option>
                  <option value="seguro">Seguro Agrícola</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción del Beneficio
                </label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  placeholder="Describe específicamente qué beneficio necesitas"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Justificación
                </label>
                <textarea
                  name="justificacion"
                  value={formData.justificacion}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  placeholder="Explica por qué necesitas este beneficio y cómo te ayudará"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div className="flex justify-end gap-4 pt-6">
                <button
                  type="button"
                  onClick={() => setShowSolicitudModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Enviar Solicitud
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}